class ProductFormAccordion extends HTMLElement {
  constructor() {
    super();

    this.details = null;
    this.summary = null;
    this.content = null;

    this.isOpen = true;
    this.isAnimating = false;
    this.transitionTimeout = null;
    this.handleTransitionEnd = null;

    this.handleSummaryClick = this.handleSummaryClick.bind(this);
    this.handleOptionChange = this.handleOptionChange.bind(this);
  }

  connectedCallback() {
    this.details = this.querySelector(":scope > .product-form__accordion-details");

    this.summary = this.details?.querySelector(":scope > .product-form__accordion-summary");

    this.content = this.details?.querySelector(":scope > .product-form__accordion-content");

    if (!this.details || !this.summary || !this.content) {
      return;
    }

    this.details.open = true;

    this.isOpen = !this.hasAttribute("closed");

    this.summary.removeEventListener("click", this.handleSummaryClick);

    this.summary.addEventListener("click", this.handleSummaryClick);

    this.removeEventListener("change", this.handleOptionChange);
    this.addEventListener("change", this.handleOptionChange);

    this.setupColorSync();

    this.syncState();
  }

  disconnectedCallback() {
    this.summary?.removeEventListener("click", this.handleSummaryClick);

    this.removeEventListener("change", this.handleOptionChange);

    this.colorSyncObserver?.disconnect();
    this.colorSyncObserver = null;

    this.cleanupTransition();
  }

  setupColorSync() {
    const colorHeaderValue = this.querySelector(
      ":scope > .product-form__accordion-details > .product-form__accordion-summary [data-color-selected-value]",
    );

    const colorPropertyInput = this.querySelector(":scope > [data-color-property-input]");

    const colorPropertyCodeInput = this.querySelector(":scope > [data-color-property-code-input]");

    if (!colorHeaderValue && !colorPropertyInput && !colorPropertyCodeInput) {
      return;
    }

    const syncFromSource = () => {
      const sourceElements = this.querySelectorAll(".product-color-groups__selected-color");

      let selectedText = "";

      sourceElements.forEach((element) => {
        const text = element.textContent.trim();

        if (text) {
          selectedText = text;
        }
      });

      if (!selectedText) {
        return;
      }

      const labelParts = selectedText.split(" - ");
      const colorName = labelParts[0].trim();

      if (colorHeaderValue) {
        colorHeaderValue.textContent = selectedText;
      }

      if (colorPropertyInput) {
        colorPropertyInput.value = selectedText;
      }

      if (colorPropertyCodeInput) {
        const labelParts = selectedText.split(" - ");

        colorPropertyCodeInput.value = labelParts.length > 1 ? labelParts[labelParts.length - 1].trim() : "";
      }
    };

    syncFromSource();

    const contentRoot = this.content || this;

    this.colorSyncObserver = new MutationObserver(syncFromSource);

    this.colorSyncObserver.observe(contentRoot, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });
  }

  handleOptionChange(event) {
    const optionInput = event.target.closest("[data-product-option-input]");

    if (optionInput) {
      const position = optionInput.getAttribute("data-option-position");
      const value = optionInput.getAttribute("data-option-value");

      this.updateSelectedValue("data-option-selected-value", position, value);

      return;
    }

    const propertyInput = event.target.closest("[data-product-property-input]");

    if (propertyInput) {
      const rawValue = propertyInput.getAttribute("data-property-value");

      const displayValue = this.formatDeliveryDisplayValue(rawValue);

      this.updateSelectedValue("data-property-selected-value", null, displayValue);
    }
  }

  formatDeliveryDisplayValue(rawValue) {
    if (!rawValue || rawValue.indexOf("|") === -1) {
      return rawValue;
    }

    const [title, , note] = rawValue.split("|").map((part) => part.trim());

    if (note === "Included") {
      return `${title} (${note})`;
    }

    return title;
  }

  updateSelectedValue(attributeName, position, value) {
    const selector = position ? `[${attributeName}="${position}"]` : `[${attributeName}]`;

    this.querySelectorAll(selector).forEach((element) => {
      element.textContent = value;
    });
  }

  syncState() {
    this.details.open = true;

    this.classList.toggle("is-open", this.isOpen);
    this.classList.toggle("is-closed", !this.isOpen);

    this.details.classList.toggle("open", this.isOpen);
    this.content.classList.toggle("is-open", this.isOpen);

    this.summary.setAttribute("aria-expanded", String(this.isOpen));

    if (this.isOpen) {
      this.content.style.removeProperty("height");
      this.content.style.removeProperty("opacity");
      this.content.style.removeProperty("overflow");
      this.content.style.removeProperty("visibility");
      this.content.removeAttribute("aria-hidden");
    } else {
      this.content.style.height = "0px";
      this.content.style.opacity = "0";
      this.content.style.overflow = "hidden";
      this.content.style.visibility = "hidden";
      this.content.setAttribute("aria-hidden", "true");
    }

    this.isAnimating = false;
  }

  handleSummaryClick(event) {
    if (event.target.closest("a, button, input, select, textarea, label")) {
      return;
    }

    event.preventDefault();

    if (this.isAnimating) {
      return;
    }

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen || this.isAnimating) {
      return;
    }

    this.cleanupTransition();

    this.isOpen = true;
    this.isAnimating = true;

    this.details.open = true;
    this.details.classList.add("open");
    this.content.classList.add("is-open");

    this.classList.add("is-open");
    this.classList.remove("is-closed");

    this.summary.setAttribute("aria-expanded", "true");

    this.content.removeAttribute("aria-hidden");
    this.content.style.visibility = "visible";
    this.content.style.overflow = "hidden";
    this.content.style.height = "0px";
    this.content.style.opacity = "0";

    this.content.getBoundingClientRect();

    requestAnimationFrame(() => {
      this.content.style.height = `${this.content.scrollHeight}px`;
      this.content.style.opacity = "1";
    });

    this.waitForTransition(() => {
      this.content.style.removeProperty("height");
      this.content.style.removeProperty("opacity");
      this.content.style.removeProperty("overflow");
      this.content.style.removeProperty("visibility");

      this.isAnimating = false;
    });
  }

  close() {
    if (!this.isOpen || this.isAnimating) {
      return;
    }

    this.cleanupTransition();

    this.isOpen = false;
    this.isAnimating = true;

    this.content.style.height = `${this.content.scrollHeight}px`;
    this.content.style.opacity = "1";
    this.content.style.overflow = "hidden";
    this.content.style.visibility = "visible";

    this.content.getBoundingClientRect();

    requestAnimationFrame(() => {
      this.details.classList.remove("open");
      this.content.classList.remove("is-open");

      this.classList.remove("is-open");
      this.classList.add("is-closed");

      this.summary.setAttribute("aria-expanded", "false");

      this.content.style.height = "0px";
      this.content.style.opacity = "0";
    });

    this.waitForTransition(() => {
      this.details.open = true;

      this.content.style.height = "0px";
      this.content.style.opacity = "0";
      this.content.style.overflow = "hidden";
      this.content.style.visibility = "hidden";
      this.content.setAttribute("aria-hidden", "true");

      this.isAnimating = false;
    });
  }

  waitForTransition(callback) {
    let completed = false;

    const finish = () => {
      if (completed) {
        return;
      }

      completed = true;

      this.cleanupTransition();
      callback();
    };

    this.handleTransitionEnd = (event) => {
      if (event.target !== this.content || event.propertyName !== "height") {
        return;
      }

      finish();
    };

    this.content.addEventListener("transitionend", this.handleTransitionEnd);

    this.transitionTimeout = window.setTimeout(finish, 400);
  }

  cleanupTransition() {
    if (this.handleTransitionEnd && this.content) {
      this.content.removeEventListener("transitionend", this.handleTransitionEnd);
    }

    if (this.transitionTimeout) {
      window.clearTimeout(this.transitionTimeout);
    }

    this.handleTransitionEnd = null;
    this.transitionTimeout = null;
  }
}

if (!customElements.get("product-form-accordion")) {
  customElements.define("product-form-accordion", ProductFormAccordion);
}

// The PDP's wishlist CTA opens the same colour-select drawer the product card uses. The card reads
// its data off the surrounding card element; here it all sits on the button itself. It needs its own
// attribute because product-card.js binds every [data-add-to-wishlist] and stops propagation before
// bailing out when there is no card around it, which would swallow this click.
document.addEventListener("click", (event) => {
  const button = event.target.closest("[data-pdp-add-to-wishlist]");

  if (!button) return;

  event.preventDefault();

  // the drawer prefers the colour-family image, but a product with no product group has no colour
  // family at all, so the largest gallery image stands in
  const gallery = [...document.querySelectorAll("[data-product-media] img, .media-gallery img")].sort(
    (a, b) => b.naturalWidth - a.naturalWidth,
  )[0];

  const detail = {
    onAdd: () => button.setAttribute("aria-pressed", "true"),
    image: gallery?.currentSrc || gallery?.src || "",
    imageAlt: gallery?.alt || "",
    productId: button.dataset.productId,
    productGroupId: button.dataset.productGroupId || "",
    color: button.dataset.color || "",
    swymProductId: button.dataset.wishlistProductId || "",
    swymVariantId: button.dataset.wishlistVariantId || "",
    swymProductUrl: button.dataset.wishlistProductUrl || "",
  };

  document.dispatchEvent(new CustomEvent("drawer:before-open", { detail: { name: "wishlist" } }));
  document.dispatchEvent(new CustomEvent("wishlist:open", { detail }));
});
